import { CopyOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import {
  FishingReport,
  FishingReportsFetchParams,
  OrganizationFishingReportsFacets,
} from '@omniafishing/core';
import { Pagination, Select } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { isPending, LoadingState } from '../../constants/loading_state';
import { useDimensions } from '../../hooks/use_dimensions';
import { useQueryString } from '../../hooks/use_query_string';
import { useUserOrganizations } from '../../hooks/use_user_organizations';
import { apiV1 } from '../../lib/api';
import { errorHandler } from '../../lib/error_handler';
import { fitBounds, getBounds } from '../../lib/fit_bounds';
import { hasAccessToken } from '../../redux/auth';
import { FlashMessageActions } from '../../redux/flash_message';
import { getOrganization } from '../../redux/organizations';
import { RoutePaths } from '../../routes';
import { PER_PAGE_DEFAULT } from '../fishing_reports_list/fishing_reports_list';
import Loading from '../loading/loading';
import { MapDefault } from '../map_default/map_default';
import { OmniaButton } from '../omnia_button/omnia_button';
import { SEO } from '../seo/seo';
import { MemberList } from './member_list';
import styles from './org_detail.less';
import { OrgDetailFishingReportsList } from './org_detail_fishing_reports_list';
import { OrgInfoList } from './org_info_list';

export const OrgDetail = (props: { slug: string }) => {
  const { slug } = props;
  const dispatch = useDispatch();
  const organization = useSelector(getOrganization);
  const isLoggedIn = useSelector(hasAccessToken);
  const { userOrganizations, joinOrganization } = useUserOrganizations();
  const location = useLocation();

  const divRef = useRef<HTMLDivElement>();
  const { pushQueryString } = useQueryString();

  const queryParams = queryString.parse(location.search);
  const { page, per_page } = queryParams;
  const currentPage = Number(page as string) || 1;
  const perPage = Number(per_page as string) || PER_PAGE_DEFAULT;

  const initialFacets: OrganizationFishingReportsFacets = {
    seasons: [],
    species: [],
    waterbodies: [],
  };
  const [fishingReports, setFishingReports] = useState([] as FishingReport[]);
  const [facets, setFacets] = useState(initialFacets);
  const [totalCount, setTotalCount] = useState(undefined as number);
  const [loadingState, setLoadingState] = useState(LoadingState.NOT_STARTED);
  const [selectedSpecies, setSelectedSpecies] = useState(undefined as string);
  const [selectedSeason, setSelectedSeason] = useState(undefined as string);
  const [selectedWaterbody, setSelectedWaterbody] = useState(undefined as string);

  const fetchOrgFishingReports = async (url_slug: string, params?: FishingReportsFetchParams) => {
    setLoadingState(LoadingState.PENDING);
    try {
      const res = await apiV1.organizationFishingReportsFetch(url_slug, params);
      setFishingReports(res.data.data.fishing_reports);
      setFacets(res.data.metadata.facets);
      setTotalCount(res.data.metadata.total_count);
      setLoadingState(LoadingState.DONE);
    } catch (e) {
      errorHandler('ERROR: fetchOrganizations', e);
      setLoadingState(LoadingState.ERROR);
    }
  };

  useEffect(() => {
    const fetchParams: FishingReportsFetchParams = {
      per_page: perPage,
      page: currentPage,
    };
    if (selectedSpecies) {
      fetchParams.species = selectedSpecies;
    }
    if (selectedSeason) {
      fetchParams.season = selectedSeason;
    }
    if (selectedWaterbody) {
      fetchParams.waterbody = selectedWaterbody;
    }

    fetchOrgFishingReports(slug, fetchParams);
  }, [slug, selectedSpecies, selectedSeason, selectedWaterbody, currentPage]);

  const selectSeasons = facets.seasons;
  const selectSpecies = facets.species;
  const waterbodies = facets.waterbodies;

  const hasFilters = selectedSpecies || selectedSeason || selectedWaterbody;

  const onPaginationChange = (newPage: number) => {
    divRef.current.scrollIntoView();
    pushQueryString({
      page: newPage,
    });
  };

  const resetPage = () => {
    pushQueryString({
      page: 1,
    });
  };

  const [measureRef, bounds] = useDimensions({ onResize: true });
  const mapSize = {
    ...bounds,
    height: 250,
  };

  const waterbodiesLongitudes = waterbodies.map((w) => w.lng);
  const waterbodiesLatitudes = waterbodies.map((w) => w.lat);
  const bboxNums = [
    Math.min(...waterbodiesLongitudes),
    Math.min(...waterbodiesLatitudes),
    Math.max(...waterbodiesLongitudes),
    Math.max(...waterbodiesLatitudes),
  ];

  const hasWaterbodiesMap = waterbodies.length > 0 && mapSize.width > 0;

  const mapBounds = hasWaterbodiesMap ? getBounds(mapSize, bboxNums) : ({} as WebMercatorViewport);

  const singleWaterbody = waterbodies.length === 1 && waterbodies[0];

  let singleWaterbodyZoom: number;

  if (singleWaterbody?.bbox && mapSize.width && mapSize.height) {
    const bbox = singleWaterbody.bbox;
    singleWaterbodyZoom = fitBounds([bbox[0], bbox[2]], {
      width: mapSize.width,
      height: mapSize.height,
    }).zoom;
  }

  if (!organization) {
    return null;
  }

  const {
    meta_title,
    name,
    description,
    meta_description,
    image,
    img_alt,
    members,
    restrict_membership,
  } = organization;

  const belongsToOrg = userOrganizations.map((o) => o.slug).includes(slug);
  const registrationLink =
    belongsToOrg &&
    `${window.location.protocol}//${window.location.host}${RoutePaths.SIGNUP}?group=${slug}`;

  return (
    <article>
      <SEO title={meta_title || name} description={meta_description || description} />

      <h1 className={styles.heading}>{name}</h1>
      {isLoggedIn && !belongsToOrg && !restrict_membership && (
        <div className={styles.button}>
          <OmniaButton
            kind="tertiary"
            size="md"
            onClick={() => {
              joinOrganization.mutate(slug);
            }}
          >
            <UsergroupAddOutlined /> Join Group
          </OmniaButton>
        </div>
      )}

      <div className={styles.orgInfo}>
        <section className={styles.details} ref={divRef}>
          {image && <img src={image} alt={img_alt || ''} className={styles.img} />}
          <p className={styles.description}>{description}</p>
          <MemberList members={members} />
          <OrgInfoList organization={organization} />

          {belongsToOrg && (
            <>
              <p>Want to invite others to join? Copy this url and share it.</p>
              <CopyToClipboard
                text={registrationLink}
                onCopy={() => {
                  dispatch(
                    FlashMessageActions.FLASH_MESSAGE_SET({
                      header: 'Link Copied',
                      subheader: 'Your organization link has been copied',
                    })
                  );
                }}
              >
                <OmniaButton kind="tertiary" size="md">
                  <CopyOutlined /> Copy
                </OmniaButton>
              </CopyToClipboard>
            </>
          )}
        </section>

        <section className={styles.fishingReports}>
          <div ref={measureRef}>{/* empty for measuring width for map */}</div>

          {isPending(loadingState) ? (
            <Loading text="Loading Group Fishing Reports" />
          ) : (
            <>
              <h2 className={styles.fishingReportsHeading}>Member Fishing Reports</h2>

              {hasWaterbodiesMap && (
                <div className={styles.topLakesMap}>
                  <MapDefault
                    longitude={singleWaterbody?.lng || mapBounds.longitude}
                    latitude={singleWaterbody?.lat || mapBounds.latitude}
                    zoom={singleWaterbodyZoom || mapBounds.zoom}
                    featuredMarkers={waterbodies}
                    disableScrollZoom
                    disablePinFetch
                    showLegend
                    hideGeocoder
                  />
                </div>
              )}
              <section className={styles.filters}>
                <span className={styles.filterTitle}>Filter By:</span>

                <Select
                  className={styles.select}
                  value={selectedSeason}
                  onChange={(value) => {
                    resetPage();
                    setSelectedSeason(value);
                  }}
                  placeholder="Season"
                  allowClear
                >
                  {selectSeasons.map((season) => (
                    <Select.Option value={season.name} key={season.name}>
                      {season.display_name}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  className={styles.select}
                  value={selectedSpecies}
                  onChange={(value) => {
                    resetPage();
                    setSelectedSpecies(value);
                  }}
                  placeholder="Species"
                  allowClear
                >
                  {selectSpecies.map((specie) => (
                    <Select.Option value={specie.name} key={specie.name}>
                      {specie.display_name}
                    </Select.Option>
                  ))}
                </Select>

                <Select
                  className={styles.select}
                  value={selectedWaterbody}
                  onChange={(value) => {
                    resetPage();
                    setSelectedWaterbody(value);
                  }}
                  placeholder="Lake"
                  allowClear
                >
                  {waterbodies.map((waterbody) => (
                    <Select.Option value={waterbody.id} key={waterbody.id}>
                      {waterbody.primary_name}
                    </Select.Option>
                  ))}
                </Select>

                {hasFilters && (
                  <div className={styles.clearFilterBtn}>
                    <OmniaButton
                      size="md"
                      kind="link"
                      onClick={() => {
                        resetPage();
                        setSelectedWaterbody(undefined);
                        setSelectedSeason(undefined);
                        setSelectedSpecies(undefined);
                      }}
                    >
                      Clear Filters
                    </OmniaButton>
                  </div>
                )}
              </section>
              <OrgDetailFishingReportsList
                fishingReports={fishingReports}
                hasFilters={!!hasFilters}
              />
              <Pagination
                current={currentPage}
                onChange={onPaginationChange}
                pageSize={perPage}
                total={totalCount || 0}
                showSizeChanger={false}
                hideOnSinglePage={true}
                className={styles.pagination}
              />
            </>
          )}
        </section>
      </div>
    </article>
  );
};
